import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { useUserPrefs } from '../UserPrefsProvider';
import { useOnboarding } from '../onboarding';

const sectionStyle = {
  textAlign: 'left',
  maxWidth: '600px',
  margin: '0 auto 1.5rem auto',
  lineHeight: '1.6',
};

const h2Style = {
  fontSize: '1.15em',
  marginBottom: '0.5rem',
  marginTop: 0,
  fontWeight: 800,
  color: 'color-mix(in srgb, var(--text-link, #228be6) 58%, var(--text-primary) 42%)',
};

const tocGroupHeadingStyle = {
  fontSize: '0.92rem',
  margin: '0.55rem 0 0.2rem',
  fontWeight: 760,
  color: 'color-mix(in srgb, var(--text-link, #228be6) 56%, var(--text-primary) 44%)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

const ulStyle = {
  paddingLeft: '1.2rem',
  margin: '0.25rem 0 0 0',
};

const tocGroupListStyle = {
  ...ulStyle,
  listStyleType: 'none',
  paddingLeft: '0.5rem',
  margin: 0,
};

const tocLinkStyle = {
  color: 'var(--text-link, #228be6)',
  textDecoration: 'none',
  fontSize: '0.95rem',
};

const HELP_SCROLL_OFFSET = 150;

const scrollToHelpSection = (id, behavior = 'smooth') => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - HELP_SCROLL_OFFSET;
  window.scrollTo({ top: Math.max(0, y), behavior });
};

function TocLink({ id, children }) {
  return (
    <li style={{ marginBottom: '0.25rem' }}>
      <a href={`#${id}`} style={tocLinkStyle} onClick={(e) => {
        e.preventDefault();
        scrollToHelpSection(id, 'smooth');
      }}>{children}</a>
    </li>
  );
}

export default function HelpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { prefs, savePrefs } = useUserPrefs();
  const { restartWizard } = useOnboarding();
  const isNotesMode = (prefs?.appMode || 'tunes') === 'notes';
  const hintsOn = prefs?.showFieldHints || false;
  const [guideMode, setGuideMode] = React.useState(isNotesMode ? 'notes' : 'tunes');
  const showTuneGuidance = guideMode === 'tunes' || guideMode === 'all';
  const showNotesGuidance = guideMode === 'notes' || guideMode === 'all';
  const tuneGuideSectionStyle = guideMode === 'all'
    ? { ...sectionStyle, border: '1px solid color-mix(in srgb, #228be6 36%, var(--card-border) 64%)', borderRadius: '12px', padding: '0.85rem 0.95rem', background: 'color-mix(in srgb, #228be6 9%, transparent)' }
    : sectionStyle;
  const notesGuideSectionStyle = guideMode === 'all'
    ? { ...sectionStyle, border: '1px solid color-mix(in srgb, #d97706 34%, var(--card-border) 66%)', borderRadius: '12px', padding: '0.85rem 0.95rem', background: 'color-mix(in srgb, #d97706 9%, transparent)' }
    : sectionStyle;

  React.useEffect(() => {
    const hash = location.hash ? location.hash.replace('#', '') : '';
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    const attemptScroll = () => {
      scrollToHelpSection(hash, 'smooth');
    };
    // Delay to ensure sections are present after route render.
    const timer = setTimeout(attemptScroll, 0);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <PageWrapperAuto>
      <div style={{ padding: '0 10px' }}>

        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
          <button className="btn-subtle search-control-btn" onClick={() => navigate(-1)} style={{ fontSize: 'var(--ui-compact-font)', padding: '5px 11px' }}>
            &larr; Back
          </button>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '1.9rem' }}>
          <h1 style={{ fontSize: '1.5em', fontWeight: 700, margin: 0 }}>Help &amp; Tutorials</h1>
        </div>

        {/* Action buttons */}
        <div className="settings-top-help-row" style={{ maxWidth: '420px', margin: '0 auto 1rem auto', flexWrap: 'wrap', gap: '8px' }}>
          <button
            onClick={restartWizard}
            className="btn-subtle search-control-btn settings-top-help-btn"
            style={{ fontSize: 'var(--ui-compact-font)', fontWeight: 700, letterSpacing: '0.01em', padding: '6px 12px' }}
          >
            ↺ Restart Setup Wizard
          </button>
<button
            onClick={() => {
              savePrefs({ ...prefs, showFieldHints: !hintsOn });
            }}
            className={`btn-subtle search-control-btn settings-top-hints-btn ${hintsOn ? 'search-control-active' : ''}`}
            style={{
              fontSize: 'var(--ui-compact-font)',
              fontWeight: 700,
              letterSpacing: '0.01em',
              padding: '6px 12px',
            }}
          >
            {hintsOn ? 'Field Hints: ON' : 'Field Hints: OFF'}
          </button>
        </div>

        <div style={{ ...sectionStyle, textAlign: 'center', fontSize: '0.85em', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
          <p style={{ margin: 0 }}>
            <strong>Restart Setup Wizard</strong> re-runs onboarding to change your mode, genres, or feature level.
            <strong> Field Hints</strong> show descriptions below each field across the app.
            To switch between Simple and Full mode, go to <strong>Settings</strong>.
          </p>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid var(--border-color, #ccc)', maxWidth: '600px', margin: '0 auto 1.5rem auto' }} />

        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.3em', margin: 0 }}>Getting Started Guide</h2>
        </div>

        <div id="mode-guidance" style={{ ...sectionStyle, textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ marginBottom: '0.45rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Mode-Specific Guidance</div>
          <div className="mode-switcher-pill">
            <button className={`mode-switcher-btn ${guideMode === 'tunes' ? 'mode-switcher-btn-active' : ''}`} onClick={() => setGuideMode('tunes')}>Tunes</button>
            <button className={`mode-switcher-btn ${guideMode === 'notes' ? 'mode-switcher-btn-active' : ''}`} onClick={() => setGuideMode('notes')}>Notes</button>
            <button className={`mode-switcher-btn ${guideMode === 'all' ? 'mode-switcher-btn-active' : ''}`} onClick={() => setGuideMode('all')}>Both</button>
          </div>
        </div>

        {/* Table of Contents */}
        <div style={{ ...sectionStyle, marginBottom: '1.5rem' }}>
          <div style={tocGroupHeadingStyle}>Start Here</div>
          <ul style={tocGroupListStyle}>
            <TocLink id="overview">Overview</TocLink>
            <TocLink id="general-workflows">General Workflows</TocLink>
            <TocLink id="mode-guidance">Mode-Specific Guidance</TocLink>
          </ul>
          <div style={tocGroupHeadingStyle}>Add &amp; Find</div>
          <ul style={tocGroupListStyle}>
            {showTuneGuidance && <TocLink id="adding-tunes">Adding Tunes</TocLink>}
            {showTuneGuidance && <TocLink id="importing">Importing from TheSession.org</TocLink>}
            {showTuneGuidance && <TocLink id="spreadsheet-import">Spreadsheet Import</TocLink>}
            {showNotesGuidance && <TocLink id="adding-notes">Adding Notes</TocLink>}
            {showTuneGuidance && <TocLink id="search-filters">Search &amp; Filters for Tunes</TocLink>}
            {showNotesGuidance && <TocLink id="notes-search">Search &amp; Filters for Notes</TocLink>}
            {showTuneGuidance && <TocLink id="tune-details">Viewing &amp; Editing Tunes</TocLink>}
            {showTuneGuidance && <TocLink id="lyrics">Lyrics &amp; Chords</TocLink>}
            {showNotesGuidance && <TocLink id="viewing-notes">Viewing &amp; Editing Notes</TocLink>}
            {showNotesGuidance && <TocLink id="note-linking">Internal Note Links</TocLink>}
            {showTuneGuidance && <TocLink id="search-tools">Search Tools</TocLink>}
          </ul>
          <div style={tocGroupHeadingStyle}>Organize</div>
          <ul style={tocGroupListStyle}>
            {showTuneGuidance && <TocLink id="sets">Sets</TocLink>}
            {showNotesGuidance && <TocLink id="folders">Folders</TocLink>}
            {showTuneGuidance && <TocLink id="programs">Programs</TocLink>}
            {showTuneGuidance && <TocLink id="sessions">Sessions</TocLink>}
            {showTuneGuidance && <TocLink id="recordings">Recordings</TocLink>}
          </ul>
          <div style={tocGroupHeadingStyle}>Share &amp; Customize</div>
          <ul style={tocGroupListStyle}>
            <TocLink id="send-export">Send &amp; Export</TocLink>
            <TocLink id="sampler-sets">Sampler Sets</TocLink>
            <TocLink id="following-sharing">Following &amp; Sharing</TocLink>
            <TocLink id="your-profile">Your Profile</TocLink>
            <TocLink id="tags">Tags</TocLink>
            <TocLink id="modes">App Modes</TocLink>
            <TocLink id="customization">Customization</TocLink>
            <TocLink id="offline">Offline Use</TocLink>
          </ul>
        </div>

        {/* Overview */}
        <div id="overview" style={sectionStyle}>
          <h2 style={h2Style}>Overview</h2>
          <p>Most tools make you choose between power and speed. A spreadsheet is flexible but slow to query. A full-featured editor is expressive but heavyweight. A community site like TheSession is great for discovery but it isn't yours. <strong>AllMyTunes is built around a different premise: capture fast, categorize lightly, find it later.</strong> Everything in the app is designed to get out of your way so the database grows naturally as you use it.</p>

          <p><strong>Tunes mode</strong> is a personal repertoire database — not a community, not a discovery tool. Sites like TheSession.org are excellent for finding tunes and learning ABC notation, but they don't know which tunes you play, how well you know them, which sets you've built, or when you last played a session. AllMyTunes does. You add what's yours, tag it your way, build your own sets and programs, log your sessions, and attach your own recordings. It works for any genre and any instrument — Irish, Old-Time, Bluegrass, klezmer, whatever you play.</p>

          <p><strong>Notes mode</strong> is a personal knowledge base — not a replacement for a full writing tool like Notion or Obsidian, but something faster and lighter. The goal is capture and retrieval: get an idea, a link, a task, or a reference into the app quickly, give it a type and a tag, and find it again when you need it. The editor supports enough formatting to be useful (headings, lists, bold, links between notes) without demanding that you think about structure before you can save anything.</p>

          <p>The two modes are independent and have their own terminology, fields, and organization. Switch between them any time from the nav bar. Your data is always tagged by mode so nothing is ever lost or mixed up.</p>
        </div>

        <div id="general-workflows" style={sectionStyle}>
          <h2 style={h2Style}>General Workflows</h2>
          <ul style={ulStyle}>
            <li>Add entries quickly and fill only the fields you need</li>
            <li>Organize entries with tags, statuses, ratings, and collections</li>
            <li>Use bulk actions on Search pages for tagging, status updates, deletion, and collection creation</li>
            <li>Open detail pages to edit inline, attach files, and manage metadata</li>
            <li>Share entries and collections with people you follow</li>
            <li>Work offline and let changes sync when back online</li>
          </ul>
        </div>

        {/* Mode-specific sections in topic order */}
        {showTuneGuidance && (
          <div id="adding-tunes" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Adding Tunes</h2>
              <p>Tap <strong>+</strong> in the nav bar to add a tune. Enter a title and fill in whatever fields you like &mdash; genre, type, key, status, rating, notes, and more.</p>
              <p>Track your learning progress with <strong>Status</strong> (Need to Learn, Learning, Learned) and <strong>Rating</strong> (1&ndash;5 stars) to keep your library organized as it grows.</p>
              <p>You can customize which fields appear on the Add page in <strong>Settings &gt; Customize Add Page</strong>.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="importing" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Importing from TheSession.org</h2>
              <p>When adding a tune, start typing the title and suggestions from thesession.org will appear automatically. Tap a suggestion to import the ABC notation, type, and key.</p>
              <p>You can also paste a thesession.org URL or tune ID directly. To import an entire tunebook, go to <strong>Settings</strong> and enter a thesession.org user ID &mdash; all your bookmarked tunes will be imported at once.</p>
              <p style={{ fontSize: '0.9em', opacity: 0.8 }}>Auto-suggest can be turned on or off in Settings.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="spreadsheet-import" style={tuneGuideSectionStyle}>
            <h2 style={h2Style}>Spreadsheet Import</h2>
            <p>You can import a batch of tunes from a CSV or spreadsheet. Go to <strong>Settings &gt; Import from Spreadsheet</strong>, paste or upload your data, and map columns to fields like title, type, key, genre, and notes.</p>
            <p>This is useful for migrating an existing tune list from another app or a personal spreadsheet. Duplicate detection is included to avoid re-importing tunes you already have.</p>
          </div>
        )}

        {showNotesGuidance && (
          <div id="adding-notes" style={notesGuideSectionStyle}>
            <h2 style={h2Style}>Adding Notes</h2>
            <p>Tap <strong>+</strong> in the nav bar to add a note. Enter a title, then use the rich text editor to format your note with bold, italic, headings, and lists.</p>
            <p>Set a <strong>category</strong> to organize by topic, a <strong>type</strong> (Note, Link, File, Idea, Task, etc.), and track progress with <strong>status</strong> (ASAP, To Do, In Progress, Done).</p>
            <p>You can also add ratings, tags, attach files, record audio, and include web links. Notes can then be organized into folders and notes sessions.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="search-filters" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Search &amp; Filters for Tunes</h2>
              <p>The search page lets you find tunes quickly. Type in the search box to search across titles, notes, ABC notation, lyrics, and more.</p>
              <p>Use the filter dropdowns to narrow results:</p>
              <ul style={ulStyle}>
                <li><strong>Genre</strong> &mdash; Irish, Old-Time, Bluegrass, or your custom genres</li>
                <li><strong>Type</strong> &mdash; Jig, Reel, Hornpipe, Waltz, Song, etc.</li>
                <li><strong>Key</strong> &mdash; filter by specific key or key root (e.g. all D modes)</li>
                <li><strong>Status</strong> &mdash; Need to Learn, Learning, Learned</li>
                <li><strong>Rating</strong> &mdash; filter by star rating</li>
                <li><strong>Tags</strong> &mdash; your custom tags with AND/OR logic</li>
              </ul>
              <p>Sort by newest, oldest, or alphabetical. Filters persist between visits so you can pick up where you left off.</p>
              <p><strong>Bulk actions:</strong> Select multiple tunes to delete, tag, change status, create a set, or send to a contact all at once.</p>
              <p><strong>Random Tune:</strong> Tap the random button to jump to a surprise tune from your filtered results &mdash; great for practice sessions.</p>
          </div>
        )}

        {showNotesGuidance && (
          <div id="notes-search" style={notesGuideSectionStyle}>
            <h2 style={h2Style}>Search &amp; Filters for Notes</h2>
            <p>Search across all your notes by title and content. Use filters to narrow results by category, type, status, rating, or tags.</p>
            <p>Sort by newest, oldest, or alphabetical. Filters persist between sessions and use notes-specific terms such as categories, types, notes, and folders.</p>
            <p><strong>Quick-add note:</strong> In notes mode, a <strong>+ Quick Note</strong> button appears on the search page to capture a title, body, and tags without leaving the page.</p>
            <p><strong>Bulk actions:</strong> Select multiple notes to delete, tag, change status, create a folder, or send to a contact all at once.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="tune-details" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Viewing &amp; Editing Tunes</h2>
              <p>Tap any tune to see its full details. From here you can:</p>
              <ul style={ulStyle}>
                <li>Edit fields inline directly on the page (no separate edit page needed)</li>
                <li>Use collapsible sections for Lyrics/Chords, ABC Notation, Notes, General Info, Attachments, and Search Tools</li>
                <li>View ABC notation rendered as sheet music, and play it back with adjustable speed (0.5x&ndash;1.5x)</li>
                <li>Record audio directly and play it back with speed control</li>
                <li>View attached images and PDFs</li>
                <li>See which sets and sessions include this tune, and navigate between tunes in a set</li>
                <li>Send the tune to someone you follow</li>
              </ul>
              <p>Search and import tools are built into Tune Details: open external search sites, paste a thesession.org URL/ID to import, and review/apply imported fields before saving.</p>
              <p>In tune mode, the page is optimized for active playing: quick prev/next navigation, inline edits, and direct access to related sets and sessions.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="lyrics" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Lyrics &amp; Chords</h2>
              <p>Add lyrics and chord symbols to any tune. Tap the lyrics section on a tune's detail page to open the fullscreen editor.</p>
              <p>The editor uses a <strong>monospace (Courier) font</strong> so you can align chord symbols above lyrics with spaces. Tab inserts 5 spaces for easy chord spacing.</p>
              <p>On Tune Details, the section is collapsible and defaults open when content exists, closed when empty.</p>
          </div>
        )}

        {showNotesGuidance && (
          <div id="viewing-notes" style={notesGuideSectionStyle}>
            <h2 style={h2Style}>Viewing &amp; Editing Notes</h2>
            <p>Tap any note to view and edit it inline. The note detail page uses a clean layout with collapsible sections for metadata (category, type, status), attachments, and timestamps.</p>
            <p>The rich text editor supports bold, italic, strikethrough, headings, bullet and numbered lists, indent/outdent, font family, alignment, and direction controls.</p>
            <p>Tap the <strong>fullscreen</strong> button to open a distraction-free editor. A zen mode strips away all chrome for focused writing.</p>
            <p>All notes use consistent single line-spacing. Font size scales with your UI size setting.</p>
          </div>
        )}

        {showNotesGuidance && (
          <div id="note-linking" style={notesGuideSectionStyle}>
            <h2 style={h2Style}>Internal Note Links</h2>
            <p>Notes can link to other notes in your library. Use the <strong>link icon</strong> in the editor toolbar to search and insert an internal link to any note by title.</p>
            <p>Linked notes can be <strong>embedded</strong> inline in the body of another note, so their content appears directly inside the parent note.</p>
            <p>Each note's detail page shows a <strong>Backlinks</strong> panel listing all notes that link to it, so you can navigate your web of notes in both directions.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="search-tools" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Search Tools</h2>
              <p>When adding or viewing a tune, you can look it up on external sites like folkfriend.app (identify tunes by humming), tunepal.org, tunearch.org, bluegrasslyrics.com, and thesession.org.</p>
              <p>Tool visibility is configurable separately for <strong>Add Page</strong> and <strong>Tune Detail Page</strong> in <strong>Settings &gt; Search Tools</strong>.</p>
              <p>On Tune Details, thesession.org also supports paste-import and update workflows with version preview/apply controls.</p>
              <p>You can also <strong>add your own custom search tool URLs</strong> for sites you use regularly &mdash; configure them in Settings.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="sets" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Sets</h2>
              <p>Sets are collections of tunes grouped together &mdash; like a setlist, a practice group, or tunes for a particular session. Create a set from the Sets page, or select multiple tunes on the Search page and use <strong>Create Set</strong> from bulk actions.</p>
              <p>Each set can have a title, notes, rating, and tags. Tap a set to see its tunes, then use the Prev/Next buttons to move through them. You can also search and filter sets by genre, type, rating, or tags.</p>
          </div>
        )}

        {showNotesGuidance && (
          <div id="folders" style={notesGuideSectionStyle}>
            <h2 style={h2Style}>Folders</h2>
            <p>Folders are collections of notes grouped together &mdash; like a project, a topic, or a reading list. Create a folder from the Folders page, or select multiple notes on the Search page and use <strong>Create Folder</strong> from bulk actions.</p>
            <p>Each folder can have a title, notes, rating, and tags. Search and filter folders just like notes.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="programs" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Programs</h2>
              <p>Programs are reusable templates for building sets. Instead of picking tunes one by one, you define <strong>named slots</strong> &mdash; like "Opening reel", "Slow air", "Closing jig" &mdash; and then fill each slot from your library.</p>
              <p>This is great for:</p>
              <ul style={ulStyle}>
                <li>Planning a session with a consistent structure</li>
                <li>Creating varied setlists from the same template each week</li>
                <li>Organizing a concert or recital program</li>
                <li>Teaching &mdash; defining a lesson plan structure that students fill with their own tunes</li>
              </ul>
              <p>Create a program from the Sets page with <strong>+ New Program</strong>. Add slots, give them names, then tap <strong>Use Program</strong> to fill each slot by searching your library. The result is saved as a regular set you can use like any other.</p>
              <p>Programs appear with a <strong style={{ color: '#9c36b5' }}>PROGRAM TEMPLATE</strong> badge. Sets created from programs show a <strong style={{ color: '#2b8a3e' }}>PROGRAM</strong> badge and link back to their template.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="sessions" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Sessions</h2>
              <p>Sessions let you log what you played and when. Start a session from the Sessions page &mdash; give it a location or name (e.g. "Tuesday session at the pub") and it begins recording time.</p>
              <p>While a session is active, any tunes you add or view can be tagged with that session. End the session when you're done and it logs the duration automatically. Over time, your session history becomes a record of your playing activity.</p>
              <p style={{ fontSize: '0.92rem', opacity: 0.9 }}>Example: start "Tuesday session at the pub", then open or add a tune and confirm the prompt to tag it to that active session.</p>
          </div>
        )}

        {showTuneGuidance && (
          <div id="recordings" style={tuneGuideSectionStyle}>
              <h2 style={h2Style}>Recordings</h2>
              <p>Record audio directly in the app &mdash; great for capturing how a tune sounds, recording a session, or noting a tricky passage. Tap <strong>Start Recording</strong> on the Add page or on a tune's detail page.</p>
              <p>Recordings are saved to your tune and can be played back with <strong>adjustable speed (0.5x&ndash;1.5x)</strong>, perfect for slowing down a fast tune to learn it.</p>
          </div>
        )}

        <div id="send-export" style={sectionStyle}>
          <h2 style={h2Style}>Send &amp; Export</h2>
          <p>From any item or set detail page, tap <strong>Send / Export</strong> to open the sharing modal. Options include:</p>
          <ul style={ulStyle}>
            <li><strong>Send to contact:</strong> Share with someone you follow — they'll receive it as an inbox item</li>
            <li><strong>Copy as text:</strong> Copy a formatted text summary to your clipboard</li>
            <li><strong>Share via system sheet:</strong> Use your device's native share menu (iOS/Android)</li>
          </ul>
          <p><strong>Bulk send:</strong> Select multiple items on the Search page and use bulk actions to send them all at once.</p>
          <p><strong>Archive export (ZIP):</strong> Go to <strong>Search &gt; Export</strong> (or the export option in the Send modal) to download your entire library as a ZIP file. Each item is saved as a Markdown file plus a <code>database.json</code> with all structured data — useful for backups or moving to another tool.</p>
        </div>

        {/* Sampler sets */}
        <div id="sampler-sets" style={sectionStyle}>
          <h2 style={h2Style}>Sampler Sets</h2>
          <p>You're automatically following the <strong>allmytunes</strong> account, which has curated sampler sets &mdash; collections of popular items organized by genre.</p>
          <p>To browse and import them:</p>
          <ul style={ulStyle}>
            <li>Go to the <strong>Social</strong> page</li>
            <li>Tap <strong>allmytunes</strong> in your Following list</li>
            <li>Expand any set and tap <strong>Import Set</strong> to copy it (with all its items) into your library</li>
          </ul>
        </div>

        {/* Shared sections for both modes */}
        <div id="following-sharing" style={sectionStyle}>
          <h2 style={h2Style}>Following &amp; Sharing</h2>
          <p>Go to the <strong>Social</strong> page to connect with other musicians and share your library.</p>
          <ul style={ulStyle}>
            <li><strong>Find someone:</strong> Enter their username in the search box on the Social page to visit their profile</li>
            <li><strong>Follow:</strong> Tap "Follow" on their profile. If they play multiple genres, you can choose which ones you want to follow</li>
            <li><strong>Browse their library:</strong> Once you follow someone, you can view their public items and sets on their profile page</li>
            <li><strong>Send items:</strong> Open any item or set and tap "Send" to share it with someone you follow. You can also send multiple items at once using bulk actions on the Search page</li>
            <li><strong>Receive:</strong> When someone sends you something, a notification appears on the Social page with "Accept" and "Deny" buttons. Accepted items are copied into your library with a note showing who sent them</li>
          </ul>
          <p>Your <strong>Following</strong> list on the Social page shows everyone you follow. Tap a name to visit their profile.</p>
        </div>

        <div id="your-profile" style={sectionStyle}>
          <h2 style={h2Style}>Your Profile</h2>
          <p>Set up your profile on the <strong>Social</strong> page &mdash; add a username, display name, bio, location, instruments, and photo so other musicians can find and recognize you.</p>
          <p>Control what others can see:</p>
          <ul style={ulStyle}>
            <li><strong>Profile visibility:</strong> Public (others can search for you and view your profile) or Private (hidden from everyone)</li>
            <li><strong>Item visibility:</strong> Share all, none, specific genres, or hand-pick individual items</li>
            <li><strong>Set visibility:</strong> Same options &mdash; control which sets are visible on your public profile</li>
          </ul>
        </div>

        <div id="tags" style={sectionStyle}>
          <h2 style={h2Style}>Tags</h2>
          <p>Add custom tags to any item for flexible cross-cutting organization. Tags work across search filters &mdash; use AND logic (must have all selected tags) or OR logic (must have any).</p>
          <p>Manage your tag list in <strong>Settings</strong>. Tags you use are tracked automatically.</p>
        </div>

        <div id="modes" style={sectionStyle}>
          <h2 style={h2Style}>App Modes</h2>
          <p>AllMyTunes has two modes you can switch between using the <strong>Mode</strong> switcher in Settings or the nav bar:</p>
          <ul style={ulStyle}>
            <li><strong>Tunes mode:</strong> Full music library with ABC notation, key signatures, TheSession.org integration, setlists, session logging, and recordings with speed control</li>
            <li><strong>Notes mode:</strong> General-purpose note-taking with rich text, categories, task statuses (ASAP, To Do, In Progress, Done), folders, internal linking, and flexible organization</li>
          </ul>
          <p>Each mode has its own terminology (tunes/notes, sets/folders, genres/categories) and field layout. Your data is tagged by mode so nothing is lost when switching.</p>
        </div>

        <div id="customization" style={sectionStyle}>
          <h2 style={h2Style}>Customization</h2>
          <ul style={ulStyle}>
            <li><strong>Themes:</strong> Light, Dark, Sepia, or Blueprint</li>
            <li><strong>UI Size:</strong> Small, Medium, or Large</li>
            <li><strong>Genres &amp; Types:</strong> Add your own custom categories in Settings</li>
            <li><strong>Add Page Fields:</strong> Choose which fields appear when adding items in Settings &gt; Customize Add Page</li>
            <li><strong>Search Tools:</strong> Toggle built-in tools and add your own custom search tool URLs</li>
          </ul>
        </div>

        <div id="offline" style={sectionStyle}>
          <h2 style={h2Style}>Offline Use</h2>
          <p>Your data is cached locally so you can browse and search even without internet. Changes sync automatically when you're back online. Use <strong>Settings &gt; Download All Data</strong> to pre-cache everything for fully offline use.</p>
        </div>

        <div style={{ ...sectionStyle, fontSize: '0.85em', opacity: 0.7, marginTop: '2rem', borderTop: '1px solid var(--card-border, #ccc)', paddingTop: '1rem' }}>
          <p>AllMyTunes is in active development. More features are planned and things may change. There are no guarantees about functionality or availability at this stage.</p>
          <p>Developed by David Sasso<br />
          <a href="mailto:allmytunesapp@gmail.com" style={{ color: 'var(--text-primary)' }}>allmytunesapp@gmail.com</a></p>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: '1rem' }}>
          <button className="btn-subtle search-control-btn" onClick={() => navigate(-1)} style={{ fontSize: 'var(--ui-compact-font)', padding: '5px 11px' }}>
            &larr; Back
          </button>
        </div>

      </div>
    </PageWrapperAuto>
  );
}
